/*
 * This file is part of glowingblue/orell-thalia-common.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import extractText from 'flarum/common/utils/extractText';
import TourStepBody from '../components/TourStepBody';
import TourProgress from '../components/TourProgress';
import loadDynLib from '../../common/helpers/loadDynLib';

/**
 * Placement of a step relative to the element it is attached to. Shepherd forwards these
 * straight to Floating UI, so the set matches Floating UI's placements.
 */
export type ShepherdPlacement =
	| 'auto'
	| 'auto-start'
	| 'auto-end'
	| 'top'
	| 'top-start'
	| 'top-end'
	| 'bottom'
	| 'bottom-start'
	| 'bottom-end'
	| 'left'
	| 'left-start'
	| 'left-end'
	| 'right'
	| 'right-start'
	| 'right-end';

/** The element a step points at, and where the step sits relative to it. */
export interface ShepherdAttachTo {
	element: string;
	on: ShepherdPlacement;
}

export interface ShepherdStepButton {
	text: string;
	classes?: string;
	/** Shepherd binds button actions to the running tour, so `this` is the tour. */
	action(this: ShepherdTour): void;
}

/** Lifecycle hooks Shepherd runs around a step. */
export interface ShepherdStepWhen {
	show?: () => void;
	hide?: () => void;
}

export interface ShepherdStepOptions {
	id?: string;
	title?: () => string;
	/** Returns the step's inner HTML; the tour mounts a Mithril component into it. */
	text?: () => string;
	attachTo?: ShepherdAttachTo;
	buttons?: ShepherdStepButton[];
	canClickTarget?: boolean;
	cancelIcon?: {
		enabled: boolean;
	};
	includeStyles?: boolean;
	/** Floating UI options; the middleware entries are opaque factories. */
	floatingUIOptions?: {
		middleware: unknown[];
	};
	scrollTo?: boolean | ScrollIntoViewOptions;
	/** Replaces Shepherd's own scrolling for this step. */
	scrollToHandler?: () => void;
	/** Awaited before the step is shown; returning nothing shows it right away. */
	beforeShowPromise?: () => Promise<void> | void;
	when?: ShepherdStepWhen;
}

export interface ShepherdStep {
	id?: string;
	/** The step's rendered element. Only present while the step is showing. */
	el: HTMLElement;
}

export interface ShepherdTourOptions {
	useModalOverlay?: boolean;
	exitOnEsc?: boolean;
	keyboardNavigation?: boolean;
	defaultStepOptions?: ShepherdStepOptions;
}

export interface ShepherdTour {
	steps: ShepherdStep[];
	currentStep: ShepherdStep;
	on(event: string, handler: () => void): void;
	addSteps(steps: ShepherdStepOptions[]): void;
	start(): void;
	complete(): void;
	next(): void;
	back(): void;
}

export interface ShepherdStatic {
	Tour: new (options?: ShepherdTourOptions) => ShepherdTour;
}

/** The Floating UI middleware factory this tour uses to offset its steps. */
export interface FloatingUICoreStatic {
	offset(value: number): unknown;
}

export interface BodyScrollLockStatic {
	disableBodyScroll(targetElement: HTMLElement): void;
	clearAllBodyScrollLocks(): void;
}

/** The `title`/`text` pair {@link HomeTourState.stepContent} spreads into a step. */
export interface ShepherdStepContent {
	title: () => string;
	text: () => string;
}

/**
 * Shepherd, Floating UI and body-scroll-lock are fetched from a CDN on demand by
 * `loadDynLib()` and install themselves as browser globals, so they have to be read off
 * `window` rather than imported. They are declared as always present because every read
 * below happens after `loadDynLib()` has resolved. Only the surface this tour drives is
 * described — none of the three ships types in this project.
 */
export interface TourWindow {
	Shepherd: ShepherdStatic;
	FloatingUICore: FloatingUICoreStatic;
	bodyScrollLock: BodyScrollLockStatic;
}

export default class HomeTourState {
	/** Set once Shepherd has loaded; `start()` polls for it. */
	tour?: ShepherdTour;

	/** Assigned alongside `tour`, so it exists whenever `tour` does. */
	initialUIPrepare!: () => void;

	/** The route the user was on when the tour started, restored when it ends. */
	originalRoute!: string;

	/** Set when the tour is force-completed on mobile, so the preference is not saved. */
	shouldNotSaveDisabled?: boolean;

	constructor() {
		loadDynLib(['shepherd.js', 'floating-ui-core', 'body-scroll-lock']).then(() => {
			const { Shepherd, FloatingUICore } = window as unknown as TourWindow;

			const tour = new Shepherd.Tour({
				useModalOverlay: true,
				exitOnEsc: true,
				keyboardNavigation: true,
				defaultStepOptions: {
					// A `scrollTo: true` used to sit here, immediately overwritten by the object
					// form further down, so only the object form was ever in effect.
					canClickTarget: false,
					cancelIcon: {
						enabled: true,
					},
					includeStyles: false,
					floatingUIOptions: {
						middleware: [FloatingUICore.offset(20)],
					},
					scrollTo: {
						behavior: 'smooth',
						block: 'end',
					},
					when: { show: () => this.addProgressIndicator() },
				},
			});

			tour.on('complete', this.endTour.bind(this));
			tour.on('cancel', this.endTour.bind(this));

			this.initialUIPrepare = () => {};

			this.addTourSteps(tour);

			const disableOnMobile = (e: MediaQueryListEvent) => {
				if (e.matches) {
					this.shouldNotSaveDisabled = true;
					tour.complete();
				}
			};
			const isMobile = window.matchMedia('(max-width: 767.98px)');
			isMobile.addEventListener('change', disableOnMobile);

			this.tour = tour;
		});
	}

	start(): void {
		if (!this.tour) {
			// Wait for Shepherd to be loaded
			const interval = setInterval(() => {
				if (this.tour) {
					clearInterval(interval);
					this.start();
				}
			}, 5);
			return;
		}

		// Save current route, so that we can get back to it when the tour is ended
		this.originalRoute = m.route.get();

		this.initialUIPrepare();
		const { bodyScrollLock } = window as unknown as TourWindow;
		bodyScrollLock.disableBodyScroll(document.body);
		this.tour.start();
	}

	endTour(): void {
		if (!this.shouldNotSaveDisabled) {
			app.session.user!.savePreferences({ homeTourDisabled: true });
		}

		delete this.shouldNotSaveDisabled;

		// Remove any tour related state of the UI
		$('#drawer').show();
		HomeTourState.closeSessionDropdown();

		const { bodyScrollLock } = window as unknown as TourWindow;
		bodyScrollLock.clearAllBodyScrollLocks();

		// Go back to the original route if we're not already there
		if (m.route.get() !== this.originalRoute) {
			m.route.set(this.originalRoute);
		}
	}

	addTourSteps(tour: ShepherdTour): void {
		tour.addSteps(this.steps);
	}

	static stepContent(stepId: string): ShepherdStepContent {
		return {
			title: () => extractText(app.translator.trans(`glowingblue-orell-thalia-common.forum.tour.home.steps.${stepId}.title`)),
			text: () => {
				setTimeout(() => {
					const step = document.querySelector(`#tour-step-container-${stepId}`);
					if (step) {
						m.mount(step, { view: () => TourStepBody.component({ stepId }) });
					}
				}, 0);

				return `<div id="tour-step-container-${stepId}"></div>`;
			},
		};
	}

	static nextButton(customAction: () => void = () => {}): ShepherdStepButton {
		return {
			text: `${extractText(
				app.translator.trans('glowingblue-orell-thalia-common.forum.tour.home.next_label')
			)} <i aria-hidden="true" class="icon far fa-arrow-circle-right Button-icon"></i>`,
			classes: 'Button Button--primary',
			action: function (this: ShepherdTour) {
				customAction();
				this.next();
			},
		};
	}

	static backButton(customAction: () => void = () => {}): ShepherdStepButton {
		return {
			text: `<i aria-hidden="true" class="icon far fa-arrow-circle-left Button-icon"></i> ${extractText(app.translator.trans('glowingblue-orell-thalia-common.forum.tour.home.back_label'))}`,
			classes: 'Button',
			action: function (this: ShepherdTour) {
				customAction();
				this.back();
			},
		};
	}

	addProgressIndicator(): void {
		// Add the progress indicator to the footer
		const currentStepElement = this.tour!.currentStep.el;
		const footer = currentStepElement.querySelector('.shepherd-footer')!;
		const progress = document.createElement('span');
		progress.className = 'shepherd-progress';
		footer.insertBefore(progress, currentStepElement.querySelector('.shepherd-button:last-child'));

		m.mount(progress, {
			view: () => TourProgress.component({ tour: this.tour }),
		});
	}

	static openSessionDropdown(): void {
		const openIt = () => {
			if (!$('.SessionDropdown').hasClass('open')) {
				$('.SessionDropdown .Dropdown-toggle').trigger('click');
			}
		};
		openIt();

		// When arriving in this step over the back button, then menu somehow
		// doesn't open. So we need to wait a bit and try again.
		// This fixes it as far as has been tested.
		setTimeout(openIt, 100);
	}

	static closeSessionDropdown(): void {
		const closeIt = () => {
			if ($('.SessionDropdown').hasClass('open')) {
				$('#header').trigger('click');
				// We click the header to close the session dropdown
				// Clicking to toggle doesn't work, because the click event is
				// captured *outside* the dropdown, and the dropdown is closed
			}
		};
		closeIt();
		setTimeout(closeIt, 100);
	}

	get steps(): ShepherdStepOptions[] {
		return [
			{
				id: 'user-profile',
				...HomeTourState.stepContent('user-profile'),
				attachTo: {
					element: '#header-secondary .item-session .Dropdown-menu',
					on: 'left-start',
				},
				buttons: [HomeTourState.nextButton()],
				// Open the menu
				beforeShowPromise: () => {
					HomeTourState.openSessionDropdown();
				},
				when: {
					show: () => this.addProgressIndicator(),
					hide: () => {
						HomeTourState.closeSessionDropdown();
					},
				},
			},
			{
				id: 'notifications',
				...HomeTourState.stepContent('notifications'),
				attachTo: {
					element: '#header-secondary .item-notifications .NotificationsDropdown',
					on: 'bottom',
				},
				buttons: [HomeTourState.backButton(), HomeTourState.nextButton()],
			},
			{
				id: 'book-collections',
				...HomeTourState.stepContent('book-collections'),
				attachTo: {
					element: '.TourFocus-book-collections',
					on: 'bottom',
				},
				buttons: [HomeTourState.backButton(), HomeTourState.nextButton()],
				beforeShowPromise: () => {
					$('#header-primary .Header-controls .item-book_collections').prepend('<div class="TourFocus TourFocus-book-collections"></div>');
				},
			},
			{
				id: 'news',
				...HomeTourState.stepContent('news'),
				attachTo: {
					element: '.TourFocus-news',
					on: 'top',
				},
				buttons: [HomeTourState.backButton(), HomeTourState.nextButton()],
				beforeShowPromise: () => {
					m.route.set('/');

					// Now wait for the news page to be rendered: check if the .item-facade-block-first element exists
					return new Promise<void>((resolve) => {
						const check = () => {
							if ($('.item-stats + .item-facade-blocks-header').length) {
								$('#drawer').show();
								$('.item-stats + .item-facade-blocks-header').prepend('<div class="TourFocus TourFocus-news"></div>');
								resolve();
							} else {
								setTimeout(check, 100);
							}
						};
						check();
					});
				},
			},
			{
				id: 'reviews',
				...HomeTourState.stepContent('reviews'),
				attachTo: {
					element: '.TourFocus-reviews',
					on: 'top',
				},
				buttons: [HomeTourState.backButton(), HomeTourState.nextButton()],
				scrollToHandler: () => {
					// As the carousel could take up almost the whole screen, we need to scroll to the title
					// to make sure we're not overlapping the tour step with the carousel
					const title = document.querySelector('.item-carousel .HomeDiscussions > h2')!;
					title.scrollIntoView({
						behavior: 'smooth',
						block: 'center',
						inline: 'center',
					});
				},
				beforeShowPromise: () => {
					$('.item-carousel .HomeDiscussions').prepend('<div class="TourFocus TourFocus-reviews"></div>');
					$('#drawer').hide();
				},
				when: {
					show: () => this.addProgressIndicator(),
					hide: () => {
						$('#drawer').show();
					},
				},
			},
			{
				id: 'discuss',
				...HomeTourState.stepContent('discuss'),
				attachTo: {
					element: '.TourFocus-discuss',
					on: 'left',
				},
				buttons: [HomeTourState.backButton(), HomeTourState.nextButton()],
				scrollTo: {
					behavior: 'smooth',
					block: 'start',
				},
				beforeShowPromise: () => {
					$('.HomePage-blocks_wrapper .HomePage-block_groups>.item-latest .HomePage-blocks .item-discussions').prepend(
						'<div class="TourFocus TourFocus-discuss"></div>'
					);
					$('#drawer').hide();
				},
				when: {
					show: () => this.addProgressIndicator(),
					hide: () => {
						$('#drawer').show();
					},
				},
			},
			{
				id: 'reading-rounds',
				...HomeTourState.stepContent('reading-rounds'),
				attachTo: {
					element: '.TourFocus-readingRound',
					on: 'right',
				},
				buttons: [HomeTourState.backButton(), HomeTourState.nextButton()],
				scrollTo: {
					behavior: 'smooth',
					block: 'start',
				},
				beforeShowPromise: () => {
					$('.ReadingRoundBlock').prepend('<div class="TourFocus TourFocus-readingRound"></div>');
					$('#drawer').hide();
				},
				when: {
					show: () => this.addProgressIndicator(),
					hide: () => {
						$('#drawer').show();
					},
				},
			},
			{
				id: 'restart-tour',
				...HomeTourState.stepContent('restart-tour'),
				attachTo: {
					element: '#header-secondary .item-session .Dropdown-menu',
					on: 'left',
				},
				buttons: [
					HomeTourState.backButton(),
					{
						text: `${extractText(app.translator.trans('glowingblue-orell-thalia-common.forum.tour.home.end_label'))} <i aria-hidden="true" class="icon far fa-check-circle Button-icon"></i>`,
						classes: 'Button Button--primary',
						action: function (this: ShepherdTour) {
							this.complete();
						},
					},
				],
				// Open the menu
				beforeShowPromise: () => {
					HomeTourState.openSessionDropdown();
				},
				when: {
					show: () => this.addProgressIndicator(),
					hide: () => {
						HomeTourState.closeSessionDropdown();
					},
				},
			},
		];
	}
}
